/**
 * الجولة الإرشادية — حالة الرحلة على الشاشات الفعلية.
 *
 * الفرق عن جولة البطاقات: تلك تشرح المنتج من خارجه، وهذه **تمشي مع الزائر
 * داخله** — نفس شاشة «قيّم أرض» ونفس شاشة النتيجة، بلا نسخة تدريبية موازية.
 *
 * لماذا لا نسخة تدريبية:
 *   شاشة تدريب منفصلة تشيخ بمفردها — يُعدَّل المنتج وتبقى هي، فتصير الجولة
 *   تعلّم منتجاً غير موجود. الجولة هنا طبقة إرشاد **فوق** الشاشة نفسها.
 *
 * ولماذا لا نعبّئ الحقول تلقائياً:
 *   من يتفرّج لا يتعلّم. المقترح يُعرض والزائر هو من يضغط.
 *
 * ---------------------------------------------------------------------------
 * لماذا مخزن وحدة لا `Context`:
 *
 * الحالة تعبر شاشتين (التقييم ثم النتيجة)، وأول تصميم وضع `Provider` في
 * `AppShell`. والثمن أن كل مَن يستعمل الجولة صار **يشترط تعديل ملف الإطار** —
 * وحين تعذّرت الكتابة على ذلك الملف سقط تسعة وثمانون اختباراً لسبب واحد:
 * غياب حاوية.
 *
 * المخزن هنا لا يحتاج حاوية إطلاقاً. أي مكوّن ينادي `useJourney()` ويعمل،
 * وشجرة التطبيق تبقى كما هي. الاقتران أقلّ، والعطب الذي وقع لا يمكن أن يتكرّر.
 * ---------------------------------------------------------------------------
 *
 * الحالة في `sessionStorage` لا `localStorage`: الجولة حدث في جلسة تصفّح لا
 * تفضيل دائم. من أغلق المتصفح انتهت جولته، ولا نطارده بها في زيارة أخرى.
 *
 * لا منطق مجال هنا — حالة عرض ونصّ فقط.
 */

import { useSyncExternalStore } from 'react';

const JOURNEY_KEY = 'nitaq.guide.journey';

/**
 * المثال الذي تمشي عليه الجولة.
 *
 * اختير حيّاً ذا صفقات كافية كي تنتهي الرحلة بنتيجة كاملة لا بامتناع — والامتناع
 * مشروح أصلاً في جولة البطاقات. ومع ذلك **الجولة لا تفترض النجاح**: إن امتنع
 * المحرك فالوقفة الأخيرة تشرح الامتناع بدل أن تنهار.
 */
export const JOURNEY_EXAMPLE = {
  city: 'الرياض',
  district: 'النرجس',
  area: '600',
  price: '3000000',
} as const;

/** عدد الوقفات على شاشة النتيجة. */
export const RESULT_STOPS = 3;

export interface JourneyApi {
  active: boolean;
  /** الوقفة الحالية على شاشة النتيجة — تُتجاهل قبل الوصول إليها. */
  stop: number;
  start: () => void;
  end: () => void;
  nextStop: () => void;
  prevStop: () => void;
}

interface JourneyState {
  active: boolean;
  stop: number;
}

function readActive(): boolean {
  try {
    return window.sessionStorage.getItem(JOURNEY_KEY) === '1';
  } catch {
    return false;
  }
}

function writeActive(value: boolean): void {
  try {
    if (value) window.sessionStorage.setItem(JOURNEY_KEY, '1');
    else window.sessionStorage.removeItem(JOURNEY_KEY);
  } catch {
    /* التخزين ممنوع — الجولة تعمل في الذاكرة ولا تنجو من تحديث الصفحة. */
  }
}

// ------------------------------------------------------------ المخزن

let state: JourneyState = { active: readActive(), stop: 0 };
const listeners = new Set<() => void>();

function emit(next: JourneyState): void {
  state = next;
  for (const listen of listeners) listen();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** يجب أن يعيد **نفس المرجع** ما لم تتغيّر الحالة — وإلا دار التصيير بلا نهاية. */
function getSnapshot(): JourneyState {
  return state;
}

export function startJourney(): void {
  writeActive(true);
  emit({ active: true, stop: 0 });
}

export function endJourney(): void {
  writeActive(false);
  emit({ active: false, stop: 0 });
}

export function nextJourneyStop(): void {
  emit({ ...state, stop: Math.min(RESULT_STOPS - 1, state.stop + 1) });
}

export function prevJourneyStop(): void {
  emit({ ...state, stop: Math.max(0, state.stop - 1) });
}

export function useJourney(): JourneyApi {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    active: snapshot.active,
    stop: snapshot.stop,
    start: startJourney,
    end: endJourney,
    nextStop: nextJourneyStop,
    prevStop: prevJourneyStop,
  };
}
